import styled from 'styled-components';
import type { FunctionComponent } from 'react';
import { paletteHelper, spacingHelper, Text, typographyHelper } from '@property.com.au/ui-kit';
import { OutboundLink } from '../../app-kit/components/OutboundLink/OutboundLink';
import { DialogModal } from '../../app-kit/components/DialogModal/DialogModal';

interface NearbySchoolsDisclaimerProps {
  opened: boolean;
  onClose: () => void;
}

const BoldBodyText = styled(Text)`
  ${typographyHelper('body01Bold')};
  color: ${paletteHelper('textSecondary')};
  padding-bottom: ${spacingHelper('large')};
`;

const BodyText = styled(Text)`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};
`;

const BodyTextExtraPadding = styled(Text)`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};
  padding-top: ${spacingHelper('large')};
`;

const StyledOutboundLink = styled(OutboundLink)`
  color: ${paletteHelper('textSecondary')};
  &:hover {
    color: ${paletteHelper('textSecondary')};
  }
`;

const CreativeCommonsLinkV3: FunctionComponent = () => {
  return (
    <StyledOutboundLink
      href="https://creativecommons.org/licenses/by/3.0/"
      outboundLinkType="creative-commons-outbound-link"
      variant="inline"
    >
      CC BY 3.0
    </StyledOutboundLink>
  );
};

const CreativeCommonsLinkV4: FunctionComponent = () => {
  return (
    <StyledOutboundLink
      href="https://creativecommons.org/licenses/by/4.0/"
      outboundLinkType="creative-commons-outbound-link"
      variant="inline"
    >
      CC BY 4.0
    </StyledOutboundLink>
  );
};

export const NearbySchoolsDisclaimer: FunctionComponent<NearbySchoolsDisclaimerProps> = ({
  opened,
  onClose,
}) => {
  return (
    <DialogModal
      opened={opened}
      onClose={onClose}
      title="School data"
      primaryButtonText="Got it"
      primaryButtonOnClick={onClose}
      gap="0"
    >
      <BoldBodyText>Grade acronyms:</BoldBodyText>
      <BodyText>P = Preschool</BodyText>
      <BodyText>K = Kindergarten</BodyText>
      <BodyText>Prep = Preparatory</BodyText>
      <BodyText>R = Reception</BodyText>
      <BodyText>PP = Pre-primary</BodyText>
      <BodyText>T = Transition</BodyText>
      <BodyTextExtraPadding>
        Data sourced from the Australian Curriculum, Assessment and Reporting Authority (ACARA)
        under license. Property.com.au makes no representations or warranties regarding the accuracy
        of this data. Please check with the school for the latest information.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        Schools are highlighted to indicate zoning only and REA does not endorse or recommend any
        listed school. Always check with the school to confirm the actual boundaries of the
        catchment zone. Distance refers to the straight line between the property and the school
        address on our database. REA makes no representations or warranties regarding the accuracy
        of this data.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; State Government of Victoria. Licensed under the Creative Commons Attribution
        International (
        <CreativeCommonsLinkV4 />) Licence.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; State of NSW (Data.NSW). Licensed under the Creative Commons Attribution
        International (
        <CreativeCommonsLinkV4 />) Licence.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; Queensland Government. Licensed under the Creative Commons Attribution International
        (
        <CreativeCommonsLinkV4 />) License.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; South Australian Government. Licensed under the Creative Commons Attribution
        International (
        <CreativeCommonsLinkV4 />) License.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; Australian Capital Territory. Licensed under the Creative Commons Attribution
        International (
        <CreativeCommonsLinkV4 />) License.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; Tasmanian Government. Licensed under the Creative Commons Attribution International (
        <CreativeCommonsLinkV3 />) License.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; Department of Education, Western Australia. Licensed under the Creative Commons
        Attribution International (
        <CreativeCommonsLinkV4 />) License.
      </BodyTextExtraPadding>
      <BodyTextExtraPadding>
        &copy; Northern Territory of Australia (Department of Education) 2017. Licensed under the
        Creative Commons Attribution International (
        <CreativeCommonsLinkV4 />) License.
      </BodyTextExtraPadding>
    </DialogModal>
  );
};
